import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { cn } from '@/utils/cn';

interface DropdownMenuProps {
  /** Rendered inside the trigger button. */
  trigger: ReactNode;
  children: (close: () => void) => ReactNode;
  label: string;
  triggerClassName?: string;
  panelClassName?: string;
  /** `listbox` for a selector, `menu` for actions. */
  role?: 'menu' | 'listbox';
}

/**
 * The one popup primitive behind the branch, notification and user menus.
 *
 * The panel is unmounted when closed rather than hidden, so its contents never sit in the tab
 * order. Alignment uses `end-0`, which resolves to the right in English and the left in Arabic —
 * no direction-specific rule anywhere.
 */
export function DropdownMenu({
  trigger,
  children,
  label,
  triggerClassName,
  panelClassName,
  role = 'menu',
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(containerRef, open, close);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup={role}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        className={cn(
          'inline-flex items-center gap-2 rounded-lg text-sm transition-colors',
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {open ? (
        <div
          id={panelId}
          role={role}
          className={cn(
            'border-line absolute end-0 z-30 mt-2 overflow-hidden rounded-lg border bg-white shadow-lg',
            panelClassName,
          )}
        >
          {children(close)}
        </div>
      ) : null}
    </div>
  );
}
